(() => {
  const rootSelector = "variant-selects[data-rd-default-native-dropdowns]";
  const selectSelector = "[data-rd-default-variant-select]";
  const appstleGuardKey = "__rdDefaultBuyboxAppstleQueryGuard";
  const appstleLifecycleKey = "__rdDefaultBuyboxAppstleLifecycleBound";
  const appstleFlag = "disableQueryParamsUpdate";
  const appstlePollInterval = 50;
  const appstleGuardTimeout = 4000;

  const hasDefaultRoot = () => Boolean(document.querySelector(rootSelector));

  const restoreAppstleQueryGuard = () => {
    const guard = window[appstleGuardKey];
    if (!guard) return;

    guard.active = false;
    if (guard.pollId !== null) window.clearTimeout(guard.pollId);
    window.removeEventListener("load", guard.onLoad);

    if (guard.applied && guard.config?.[appstleFlag] === true) {
      if (guard.hadOwnProperty) {
        guard.config[appstleFlag] = guard.originalValue;
      } else {
        delete guard.config[appstleFlag];
      }
    }

    delete window[appstleGuardKey];
  };

  const armAppstleQueryGuard = () => {
    if (window[appstleGuardKey]?.active) return;

    const guard = {
      active: true,
      applied: false,
      config: null,
      deadline: Date.now() + appstleGuardTimeout,
      hadOwnProperty: false,
      onLoad: null,
      originalValue: undefined,
      pollId: null
    };

    const apply = () => {
      guard.pollId = null;
      if (!guard.active) return;

      if (!hasDefaultRoot()) {
        restoreAppstleQueryGuard();
        return;
      }

      const config = window.RSConfig;
      if (config && typeof config === "object") {
        if (!guard.applied || guard.config !== config) {
          guard.config = config;
          guard.hadOwnProperty = Object.prototype.hasOwnProperty.call(config, appstleFlag);
          guard.originalValue = config[appstleFlag];
        }

        config[appstleFlag] = true;
        guard.applied = true;
        return;
      }

      if (Date.now() < guard.deadline) {
        guard.pollId = window.setTimeout(apply, appstlePollInterval);
      } else {
        restoreAppstleQueryGuard();
      }
    };

    guard.onLoad = apply;
    window[appstleGuardKey] = guard;
    apply();
    if (guard.active && guard.applied === false) {
      window.addEventListener("load", guard.onLoad, { once: true });
    }
  };

  const bindAppstleLifecycle = () => {
    if (window[appstleLifecycleKey]) return;

    window[appstleLifecycleKey] = true;
    window.addEventListener("pagehide", restoreAppstleQueryGuard);
    window.addEventListener("pageshow", () => {
      if (hasDefaultRoot()) armAppstleQueryGuard();
    });
  };

  const restoreWhenNoDefaultRoot = () => {
    window.setTimeout(() => {
      if (!hasDefaultRoot()) restoreAppstleQueryGuard();
    }, 0);
  };

  const getVariants = (root) => {
    const source = root.querySelector("[data-all-variants-no-high]")?.textContent;
    if (!source) return [];

    try {
      return JSON.parse(source);
    } catch (error) {
      return [];
    }
  };

  const getSelects = (root) =>
    [...root.querySelectorAll(selectSelector)].sort((first, second) => {
      const firstPosition = Number(first.closest("[data-option-position]")?.dataset.optionPosition || 0);
      const secondPosition = Number(second.closest("[data-option-position]")?.dataset.optionPosition || 0);
      return firstPosition - secondPosition;
    });

  const reconcileAvailability = (root) => {
    const variants = getVariants(root);
    const selects = getSelects(root);
    if (variants.length === 0 || selects.length === 0) return null;

    const selectedValues = selects.map((select) => select.value);
    let repairedSelect = null;

    selects.forEach((select, index) => {
      const selectedPrefix = selectedValues.slice(0, index);
      const availableValues = new Set(
        variants
          .filter(
            (variant) =>
              variant.available &&
              selectedPrefix.every((value, previousIndex) => variant.options[previousIndex] === value)
          )
          .map((variant) => variant.options[index])
      );

      [...select.options].forEach((option) => {
        const enabled = availableValues.has(option.value);
        option.disabled = !enabled;
        if (enabled) {
          option.removeAttribute('aria-disabled');
        } else {
          option.setAttribute('aria-disabled', 'true');
        }
        option.textContent = option.dataset.rdOptionLabel || option.value;
      });

      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption || selectedOption.disabled) {
        const firstAvailableOption = [...select.options].find((option) => !option.disabled);
        if (firstAvailableOption) {
          select.value = firstAvailableOption.value;
          selectedValues[index] = firstAvailableOption.value;
          repairedSelect = select;
        }
      }
    });

    return repairedSelect;
  };

  const initialize = (root) => {
    if (root.dataset.rdDefaultDropdownReady === "true") return;
    root.dataset.rdDefaultDropdownReady = "true";
    armAppstleQueryGuard();

    root.addEventListener("change", (event) => {
      if (!event.target.matches(selectSelector)) return;

      queueMicrotask(() => {
        const repairedSelect = reconcileAvailability(root);
        if (repairedSelect) repairedSelect.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    const repairedSelect = reconcileAvailability(root);
    if (repairedSelect) repairedSelect.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const initializeAll = (scope = document) => {
    const roots = [];
    if (scope.matches?.(rootSelector)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(rootSelector));
    roots.forEach(initialize);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  bindAppstleLifecycle();
  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    const section = event.target;
    const containsDefaultRoot = section.matches?.(rootSelector) || section.querySelector?.(rootSelector);
    if (containsDefaultRoot) restoreWhenNoDefaultRoot();
  });
})();
